import { c as _c } from "react/compiler-runtime";
import React, { memo, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import useProductAttributes from 'data-hooks/useProductAttributes';
import usePDPPhotos from 'data-hooks/usePDPPhotos';
import { getPriceDetailsUtil } from 'data-hooks/utils';
import useModularPDPCart from 'data-hooks/useModularPDPCart';
import { useLocation } from 'react-router-dom';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import usePDPPrice from 'data-hooks/usePDPPrice';
import convertOptionParamFormat from 'commons/convertOptionParamFormat';
import { getPriceValidUntil } from './utils';
import usePdpIsModularBYOProduct from '../state/context/usePdpIsModularBYOProduct';
import usePdpSelectedOptionsVariantSku from '../state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import usePDPBasicDetails from '../state/context/usePDPBasicDetails';
import usePDPOptions from '../state/context/usePDPOptions';
import { isUpholsteryOption } from '../helpers/pdpOptionUtils';
const PDPSEOTagsInner = ({
  isOutOfStock = false
}) => {
  // Store the initial price (which should be the final price) so that we can
  // always output this price in the JSON LD script tag even if the price
  //  changes on the client side after the initial render (e.g. if the client
  // side app crashes or the user's location impacts the price.
  const initialPrice = useRef(null);
  const {
    data: attributesData
  } = useProductAttributes();
  const {
    data: priceData = {},
    loading: priceLoading
  } = usePDPPrice();
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();
  const {
    data: modularCartData
  } = useModularPDPCart();
  const {
    data: photosData
  } = usePDPPhotos();
  const {
    data: basicDetails = {}
  } = usePDPBasicDetails();
  const {
    data: optionsData
  } = usePDPOptions();
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  const location = useLocation();
  const priceDetails = useMemo(() => {
    if (!priceData || priceLoading) {
      return {};
    }
    if (pdpIsModularBYOProduct) {
      return {
        salePriceWithOptionsPrice: modularCartData?.modularTotalPrice
      };
    }
    return getPriceDetailsUtil(priceData?.price, priceData?.promotion, priceData?.optionPrice);
  }, [priceData, priceLoading, pdpIsModularBYOProduct, modularCartData?.modularTotalPrice]);
  const calculatedPrice = priceDetails.hasTieredDiscount ? priceDetails.leastTieredDiscountedPrice : priceDetails?.salePriceWithOptionsPrice;

  // Once calculatdPrice is truthy, store it as initialPrice. If
  // initialPrice.current is truthy, the value should not be updated.
  initialPrice.current = initialPrice.current || calculatedPrice;
  const price = initialPrice.current;
  const basicDetailsName = basicDetails?.name;
  const basicDetailsSku = basicDetails?.sku;
  const selectedPhotoSrc = photosData?.selectedPhoto?.src || '';
  const metaDescription = attributesData?.SEOAttributes?.meta_description;

  // ref: https://stitchindustries.atlassian.net/browse/CON-6035
  const priceValidUntil = useMemo(() => getPriceValidUntil(priceData?.promotion), [priceData?.promotion]);
  const heroImageByUpholsteryName = useMemo(() => {
    const heroImages = basicDetails?.hero_images || [];
    return heroImages.reduce((map, heroImg) => {
      if (heroImg?.upholstery_name) {
        const key = heroImg.upholstery_name.toLowerCase().trim();
        if (!map[key]) {
          map[key] = heroImg.img?.url;
        }
      }
      return map;
    }, {});
  }, [basicDetails?.hero_images]);
  const hasVariant = useMemo(() => {
    const options = optionsData?.options || [];
    const upholsteryOption = options.find(opt => isUpholsteryOption(opt.id));
    if (!upholsteryOption?.values?.length) {
      return [];
    }
    const canonicalURL = attributesData?.SEOAttributes?.canonicalURL;
    const {
      finalOptionName: optionParamName
    } = convertOptionParamFormat(upholsteryOption.name, '');
    const selectedUpholsteryValueId = optionsData?.selectedOptions?.[upholsteryOption.id];
    const selectedUpholsteryValue = upholsteryOption.values.find(v => v.id === selectedUpholsteryValueId);
    const optionPrice = priceData?.optionPrice || 0;
    const currentUpholsteryAdditionalCost = selectedUpholsteryValue?.additional_cost || 0;
    const nonUpholsteryCost = optionPrice >= currentUpholsteryAdditionalCost ? optionPrice - currentUpholsteryAdditionalCost : optionPrice;
    return upholsteryOption.values.filter(v_0 => v_0.is_active !== 0).map(optionValue => {
      const {
        finalOptionValue: optionValueSlug
      } = convertOptionParamFormat('', optionValue.value);
      const variantUrl = canonicalURL ? `${canonicalURL}?${encodeURIComponent(optionParamName)}=${encodeURIComponent(optionValueSlug)}` : undefined;
      const variantSku = basicDetailsSku != null ? `${basicDetailsSku}${optionValue.sku || ''}` : undefined;
      const variantTotalAdditionalCost = nonUpholsteryCost + (optionValue.additional_cost || 0);
      const variantPriceDetails = priceData?.price && !priceLoading ? getPriceDetailsUtil(priceData.price, priceData.promotion, variantTotalAdditionalCost, priceData.isClearance) : null;
      const variantPrice = variantPriceDetails ? variantPriceDetails.hasTieredDiscount ? variantPriceDetails.leastTieredDiscountedPrice : variantPriceDetails.salePriceWithOptionsPrice : undefined;
      const variantImage = heroImageByUpholsteryName[optionValue.value?.toLowerCase()?.trim()] || optionValue.image || selectedPhotoSrc;
      const variantOffers = {
        '@type': 'Offer',
        priceCurrency: 'USD',
        availability: isOutOfStock ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition'
      };
      if (variantUrl) variantOffers.url = variantUrl;
      if (variantPrice != null) variantOffers.price = variantPrice;
      if (priceValidUntil) variantOffers.priceValidUntil = priceValidUntil;
      const variant = {
        '@type': 'Product',
        sku: variantSku,
        image: variantImage,
        color: optionValue.value,
        offers: variantOffers
      };
      if (basicDetailsName) {
        variant.name = `${basicDetailsName} in ${optionValue.value}`;
      }
      if (variantUrl) {
        variant['@id'] = variantUrl;
        variant.url = variantUrl;
      }
      return variant;
    });
  }, [optionsData?.options, optionsData?.selectedOptions, attributesData?.SEOAttributes?.canonicalURL, basicDetailsSku, basicDetailsName, priceData, priceLoading, priceValidUntil, isOutOfStock, selectedPhotoSrc, heroImageByUpholsteryName]);
  const jsonLD = useMemo(() => {
    const productGroup = {
      '@context': 'https://schema.org/',
      '@type': 'ProductGroup',
      name: basicDetailsName,
      description: metaDescription,
      brand: {
        '@type': 'Brand',
        name: 'Joybird'
      }
    };
    if (basicDetailsSku != null) {
      productGroup.productGroupID = basicDetailsSku;
    }
    if (attributesData?.SEOAttributes?.canonicalURL) {
      productGroup['@id'] = attributesData.SEOAttributes.canonicalURL;
      productGroup.url = attributesData.SEOAttributes.canonicalURL;
    }
    if (hasVariant.length > 0) {
      productGroup.variesBy = ['https://schema.org/color'];
      productGroup.hasVariant = hasVariant;
    } else {
      // Fallback for products without upholstery variants (e.g. tables):
      // preserve price/stock/image at the ProductGroup level so structured
      // data is not regressed vs. the previous single-Product schema.
      productGroup.image = selectedPhotoSrc || undefined;
      productGroup.sku = basicDetailsSku;
      const fallbackOffers = {
        '@type': 'Offer',
        priceCurrency: 'USD',
        availability: isOutOfStock ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition'
      };
      if (attributesData?.SEOAttributes?.canonicalURL) {
        fallbackOffers.url = attributesData.SEOAttributes.canonicalURL;
      }
      if (price != null) fallbackOffers.price = price;
      if (priceValidUntil) fallbackOffers.priceValidUntil = priceValidUntil;
      productGroup.offers = fallbackOffers;
    }
    return productGroup;
  }, [basicDetailsName, metaDescription, basicDetailsSku, attributesData?.SEOAttributes?.canonicalURL, hasVariant, isOutOfStock, price, priceValidUntil, selectedPhotoSrc]);
  const productNameFromRouterState = location?.state?.name;
  const pageTitle = basicDetails?.name || productNameFromRouterState || '';
  const jsonLDString = useMemo(() => jsonLD ? JSON.stringify(jsonLD) : null, [jsonLD]);
  return <Helmet>
      <title>{pageTitle ? `${pageTitle} | Joybird` : `Joybird`}</title>

      <meta name="description" content={attributesData?.SEOAttributes?.meta_description || ''} />

      <meta name="keywords" content={attributesData?.SEOAttributes?.meta_keywords || ''} />

      <meta property="og:type" content="product" />
      <meta property="og:title" content={pageTitle} />

      <meta property="og:description" content={attributesData?.SEOAttributes?.meta_description || ''} />

      <meta property="og:price:amount" content={price || ''} />
      <meta property="og:price:currency" content="USD" />

      <meta property="og:availability" content={isOutOfStock ? 'Out of Stock' : 'Ready to Ship'} />

      {attributesData?.SEOAttributes?.canonicalURL && <>
          <meta property="og:url" content={attributesData?.SEOAttributes?.canonicalURL} />

          <link id="canonical" href={attributesData?.SEOAttributes?.canonicalURL} rel="canonical" />
        </>}

      {photosData?.selectedPhoto?.src && <meta property="og:image" content={photosData?.selectedPhoto?.src} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${pageTitle} | Joybird`} />

      <meta name="twitter:description" content={attributesData?.SEOAttributes?.meta_description?.value || ''} />

      <meta data-rh="true" name="sku" content={`${basicDetails?.sku}${selectedOptionsVariantSku}`} />

      {photosData?.selectedPhoto?.src && <meta name="twitter:image" content={photosData.selectedPhoto.src} />}

      {jsonLDString && <script type="application/ld+json">{jsonLDString}</script>}
    </Helmet>;
};
PDPSEOTagsInner.propTypes = {
  isOutOfStock: PropTypes.bool
};
const PDPSEOTags = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SuspenseWithLoader><PDPSEOTagsInner {...props} /></SuspenseWithLoader>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default memo(PDPSEOTags);